import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { formatSize, generateString } from "../../../../config/global";
import { uploadToGcs } from "../../../../lib/gcsUpload";
import { useCreateMediaMutation } from "../../../../queries";
import { useCustomToast } from "../../../Contexts/ToastContext";
import { galleryRefetch } from "../../../../redux/Slices/scrollSlice";
import { allowedFileTypes, MAX_FILE_LIMIT } from "../../../../config/media";

import Dropzone from "./components/Dropzone";
import FileList from "./components/FileList";
import ProgressBarModal from "./components/ProgressBarModal";
import { useScrollLoadingContext } from "../../../Contexts/ScrollLoadingContext";

const UploadMedia = ({ capacityAvailability, currentFolder, onClose, disabled }) => {
  const { t } = useTranslation();
  const openDialog = useSelector((state) => state.global.openDialog);

  const { companyDetails } = useSelector((state) => state.global);
  const [capacityAtOnce, setCapacityAtOnce] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [counter, setCounter] = useState(null);
  const [files, setFiles] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [remainingCapacity, setRemainingCapacity] = useState(capacityAvailability);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [disableUpload, setDisableUpload] = useState(disabled || false);
  const uploadComplete = useRef(false);

  const { showToast } = useCustomToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { id } = useParams();
  let isCampaignUpload = openDialog.addContent

  const uploadMedia = useCreateMediaMutation(setProgress, setCounter);

  // Initialize remaining capacity when prop changes
  useEffect(() => {
    setRemainingCapacity(capacityAvailability);
  }, [capacityAvailability]);

  useEffect(() => {
    setDisableUpload(disabled);
  }, [disabled]);

  const handleFilesSelected = useCallback((newFiles) => {
    setFiles(prevFiles => {
      let upload_once_content_capacity = companyDetails.upload_once_content_capacity;
      // let upload_once_content_capacity = 1024 * 1024 * 1024;
      let availableCapacity = upload_once_content_capacity - totalSize;
      let acceptedFiles = [];
      let newFilesSize = 0;

      for (const file of newFiles) {
        if (file.size <= availableCapacity) {
          acceptedFiles.push(file);
          availableCapacity -= file.size;
          newFilesSize += file.size;
        } else {
          setCapacityAtOnce(true)
          break;
        }
      }

      const combinedFiles = [...prevFiles, ...acceptedFiles].slice(0, MAX_FILE_LIMIT);

      setTotalSize(prev => prev + newFilesSize);
      setRemainingCapacity(prev => prev - newFilesSize);

      return combinedFiles;
    });
  }, [totalSize]);


  const handleRemoveFile = useCallback((index) => {
    setFiles(prev => {
      const newFiles = [...prev];
      const removedFile = newFiles.splice(index, 1)[0];
      setTotalSize(prevSize => prevSize - removedFile.size);
      setRemainingCapacity(prev => prev + removedFile.size);
      return newFiles;
    });
  }, []);

  // Upload a single file straight to our GCS bucket via the backend's
  // /api/s3/* multipart endpoints, then register it with /media/create.
  // No AWS: the object lands in popuppush-(dev|prod)-media and we store the
  // public GCS URL. The mutation maps its own progress to 50..100, so the
  // raw upload reports 0..50 here for a smooth bar.
  const uploadHandler = useCallback(async (file) => {
    let fileType = file.type === "" ?
      file.name.slice(file.name.lastIndexOf('.') + 1) :
      file.type.includes("quicktime") ?
        file.name?.split(".")[1] :
        file.type?.split("/")[1];

    fileType = fileType?.replace(/json\+|avi\./g, match =>
      match === "json+" ? "" : "avi"
    );

    const hashedFile = generateString(30, fileType);
    if (!hashedFile) {
      // Unsupported file type — generateString returns null.
      setProgress(prev => ({ ...prev, [file.name]: "failed" }));
      setCounter(prev => prev - 1);
      return;
    }

    // Company id MUST come from the loaded company details (authoritative —
    // it matches the auth user's company_id the backend scopes uploads to in
    // S3MultipartController::assertScope). The previous module-load
    // localStorage read was stale/empty for freshly-registered users, so the
    // object key didn't start with "<company_id>/" → backend 403 → the Axios
    // interceptor treated it as a session error and forced a logout.
    const companyId = companyDetails?.company?.id || localStorage.getItem("company_id");
    if (!companyId) {
      showToast('error', t('media.media_toast_upload_failed') || 'Upload failed', 'Account still loading — please retry in a moment.');
      setProgress(prev => ({ ...prev, [file.name]: "failed" }));
      setCounter(prev => prev - 1);
      return;
    }
    const objectKey = `${companyId}/${localStorage.getItem("current_folder") || "root"}/${hashedFile}`;

    const publicUrl = await uploadToGcs(file, objectKey, (pct) => {
      setProgress(prev => ({ ...prev, [file.name]: Math.round(pct / 2) }));
    });

    await uploadMedia.mutateAsync({
      file,
      fileUrl: publicUrl,
      folderId: isCampaignUpload ? currentFolder.id : id,
    });
  }, [id, currentFolder, isCampaignUpload, uploadMedia]);

  const uploadFile = useCallback(async () => {
    if (files.length === 0) return;

    try {
      setUploading(true);
      setShowProgressModal(true);
      setCounter(files.length);
      uploadComplete.current = false;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        try {
          await uploadHandler(file);
        } catch (uploadError) {
          console.error(`Upload failed for ${file.name}:`, uploadError);
          showToast('error', t('media.media_toast_upload_failed'), t('media.media_toast_upload_failed_detail', { name: file.name }));
          setProgress(prev => ({ ...prev, [file.name]: "failed" }));
          setCounter(prev => prev - 1);
        }
      }
    } catch (error) {
      console.error('Error during upload process:', error);
      showToast('error', t('media.media_toast_upload_failed'), t('media.media_toast_upload_process_error'));
      handleReset();
    }
  }, [files, uploadHandler]);






  // Separate cleanup function
  const cleanup = useCallback(() => {
    setFiles([]);
    setProgress({});
    setTotalSize(0);
    setUploading(false);
    setCapacityAtOnce(false);
    setShowProgressModal(false);
    setRemainingCapacity(capacityAvailability);
    setCounter(null);
    uploadComplete.current = false;
    queryClient.invalidateQueries('checkMediaCapacity')
  }, [capacityAvailability]);

  // Handle upload completion
  useEffect(() => {
    if (counter === 0 && uploading && !uploadComplete.current) {
      uploadComplete.current = true;
      dispatch(galleryRefetch());
      showToast('success', t('media.media_toast_upload_complete'), t('media.media_toast_upload_complete_detail'));
      cleanup();
      onClose();
    }
  }, [counter, uploading]);

  // Check capacity limits
  useEffect(() => {
    if (totalSize > capacityAvailability) {
      showToast('warn', t('media.media_toast_capacity_exceeded'), t('media.media_toast_capacity_exceeded_detail'));
      setDisableUpload(true);
    } else {
      setDisableUpload(disabled);
    }
  }, [totalSize, capacityAvailability, showToast, disabled]);

  const progressItems = useMemo(() =>
    files.map((file, index) => ({
      file,
      index,
      progress: progress[file.name] || 0,
      status: progress[file.name] === "failed" ? "failed" : progress[file.name] === 100 ? "success" : "processing"
    })),
    [files, progress]
  );

  const handleReset = useCallback(() => {
    cleanup();
    setDisableUpload(disabled);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);
  const validateFiles = useCallback((newFiles) => {
    let validFiles = [];
    let currentSize = totalSize;
    let filesAdded = 0;

    for (const file of newFiles) {
      if (filesAdded >= MAX_FILE_LIMIT) {
        showToast('error', t('media.media_toast_file_limit_exceeded'), t('media.media_toast_file_limit_detail', { count: MAX_FILE_LIMIT }));
        break;
      }

      if (currentSize + file.size > remainingCapacity) {
        showToast('error', t('media.media_toast_capacity_exceeded'), t('media.media_toast_capacity_remaining', { size: formatSize(remainingCapacity - currentSize) }));
        continue;
      }

      if (file.size > companyDetails.upload_once_content_capacity) { // 1GB limit
        showToast('error', t('media.media_toast_file_too_large'), t('media.media_toast_file_too_large_detail', { name: file.name, gb: Math.round(companyDetails.upload_once_content_capacity / (1024 * 1024 * 1024)) }));
        continue;
      }

      validFiles.push(file);
      currentSize += file.size;
      filesAdded++;
    }

    return validFiles;
  }, [remainingCapacity, showToast, totalSize]);

  const handleFileInput = useCallback((e) => {
    if (!disabled) {
      const files = Array.from(e.target.files);
      const validFiles = validateFiles(files);
      handleFilesSelected(validFiles);
      e.target.value = ''; // Reset input
    }
  }, [validateFiles, handleFilesSelected, disabled]);




  return (
    <div className="pp-upload-body">
      <div className="d-flex flex-wrap">
        <div className={`d-flex items-center justify-center col-12 ${files.length > 0 ? "col-md-4" : "col-md-12"}`}>
          {!uploading && files.length < MAX_FILE_LIMIT && (
            <Dropzone
              onFilesSelected={handleFilesSelected}
              validateFiles={validateFiles}
              remainingCapacity={remainingCapacity}
              totalSize={totalSize}
              disabled={disableUpload}
            />
          )}
        </div>

        {files.length > 0 && (
          <div className={`col-12 ${files.length === MAX_FILE_LIMIT || uploading ? "col-md-12" : "col-md-8"}`}>
            <FileList
              files={files}
              setCapacityAtOnce={setCapacityAtOnce}
              capacityAtOnce={capacityAtOnce}
              onRemoveFile={handleRemoveFile}
              uploading={uploading}
            />
          </div>
        )}
      </div>

      {showProgressModal && (
        <ProgressBarModal progressItems={progressItems} />
      )}

      {!uploading && files.length > 0 && (
        <div className="col-12 p-3 pp-upload-footer flex justify-between items-center">
          <button
            type="button"
            className="btn btn-label-secondary me-3"
            onClick={handleReset}
            disabled={disableUpload}
          >
            {t('media.media_reset')}
          </button>
          <div className="flex gap-2">
            <label
              htmlFor="file_more"
              className="btn border   "
            >
              {uploading ? t('media.media_uploading') : t('media.media_upload_more')}
            </label>

            <input
              className="hidden"
              type="file"
              id="file_more"
              multiple
              accept={allowedFileTypes.map(ext => `.${ext}`).join(',')}
              onChange={handleFileInput}
              disabled={disabled}
            />
            <button
              type="button"
              className="btn btn-primary  "
              onClick={uploadFile}
              disabled={uploading || files.length === 0 || disableUpload}
            >
              <div className="flex gap-1">
                <div>{uploading ? t('media.media_uploading') : t('buttons.upload')}</div>
                {files.length > 0 && (
                  <div className="bg-white  text-black rounded-full w-5 h-5 text-[13px] flex items-center justify-center">
                    {files.length}
                  </div>
                )}
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(UploadMedia);



